import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { User } from "../types/auth";
import { authService } from "../services/authService";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        setLoading(true);
        const storedToken = localStorage.getItem("token");

        if (storedToken) {
          // Verify token and get user profile
          const isValid = await authService.validateToken(storedToken);

          if (isValid) {
            setToken(storedToken);
            const userProfile = await authService.getUserProfile(storedToken);

            setUser(userProfile);
          } else {
            // If token is invalid, clear it
            localStorage.removeItem("token");
            setToken(null);
            setUser(null);
          }
        }
      } catch (err) {
        console.error("Auth initialization error:", err);
        localStorage.removeItem("token");
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Login handler
  const login = async (username: string, password: string) => {
    try {
      setError(null);
      setLoading(true);

      // Get token from auth service
      const authResponse = await authService.login(username, password);

      setToken(authResponse.access_token);
      localStorage.setItem("token", authResponse.access_token);

      // Get user profile
      const userProfile = await authService.getUserProfile(
        authResponse.access_token,
      );

      setUser(userProfile);

      // Redirect to dashboard or previously attempted page
      const from = new URLSearchParams(location.search).get("from");

      navigate(from || "/dashboard", { replace: true });
    } catch (err: any) {
      setError(err.message || "Login failed. Please check your credentials.");
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Logout handler
  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    navigate("/login");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        token,
        login,
        logout,
        isAuthenticated: !!token && !!user,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
